import { Tooltip } from '@sgme/ui';
import classNames from 'classnames';
import { ChangeEvent, FC, KeyboardEvent, MouseEvent, useCallback, useContext, useState } from 'react';
import { Dropdown, DropdownMenu } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import type { RegulationData } from 'state/quotes/dashboard/model';
import type { PinnedGroup } from 'state/quotes/dashboard/selectors';
import type { GroupColor } from 'state/quotes/dashboard/tiles/tiles.model';
import styled from 'styled-components';

import { ColorItem } from '../../colorSelector/ColorItem';
import { ColorSelector } from '../../colorSelector/ColorSelector';
import { RegulationPanel } from './regulation';
import { IconWorkaround } from './result/status';
import { TermsheetButton } from './termsheet/TermsheetButton';
import { TermsheetContext } from './termsheet/termsheetContext';

interface FooterProps {
  isPinned: boolean;

  availableGroups: PinnedGroup[];
  selectedGroup?: string;
  selectedGroupColor?: GroupColor;

  hasAssociatedTab: boolean;
  onWorkspaceTabOpened: () => void;
  onPinToggled: () => void;
  onGroupChanged(group: string, color?: GroupColor): void;
  onUnGrouped(): void;
  onReprice?: () => void;
  regulationData: RegulationData;
}

export const Footer: FC<FooterProps> = ({
  hasAssociatedTab,
  isPinned,
  availableGroups,
  selectedGroup,
  selectedGroupColor,
  onPinToggled,
  onGroupChanged,
  onUnGrouped,
  onWorkspaceTabOpened,
  regulationData,
  onReprice,
}) => {
  const termsheetApi = useContext(TermsheetContext);
  return (
    <div className="card-footer py-2 px-2 d-flex flex-row align-items-center">
      <div className="flex-grow-1 ps-2 d-flex align-items-center" data-tour="quoteDashboard.tile.downloadDocs">
        {termsheetApi && (
          <TermsheetButton
            className="me-1"
            locales={termsheetApi.locales}
            quoteQuery={termsheetApi.quoteQuery}
            onSuccess={termsheetApi.onSuccess}
            onError={termsheetApi.onError}
          />
        )}
        <RegulationPanel data={regulationData} />
        {selectedGroup && selectedGroupColor && (
          <SelectedGroupColor groupName={selectedGroup} groupColor={selectedGroupColor} />
        )}
      </div>

      <Tooltip position="top" contents={<FormattedMessage id="dashboard.repriceQuote.title" />}>
        <button
          className="btn btn-xl btn-icon btn-flat-secondary my-1"
          type="button"
          onClick={onReprice}
          disabled={onReprice === undefined}
          data-tour="quoteDashboard.tile.refresh"
        >
          <em className="icon">refresh</em>
        </button>
      </Tooltip>

      <PinnedDropDown
        isPinned={isPinned}
        availableGroups={availableGroups}
        selectedGroup={selectedGroup}
        onPinToggled={onPinToggled}
        onGroupChanged={onGroupChanged}
        onUnGrouped={onUnGrouped}
      />

      <Tooltip
        position="top"
        contents={
          <FormattedMessage
            id={hasAssociatedTab ? 'dashboard.openAssociatedTab.title' : 'dashboard.reloadInNewTab.title'}
          />
        }
      >
        <button
          className="btn btn-xl btn-icon btn-flat-secondary"
          type="button"
          onClick={onWorkspaceTabOpened}
          data-e2e="redirect-to-quote-form"
          data-tour="quoteDashboard.tile.redirectToQuoteForm"
        >
          <em className="icon">{hasAssociatedTab ? 'launch' : 'add_to_photos'}</em>
        </button>
      </Tooltip>
    </div>
  );
};

Footer.displayName = 'Footer';

const SelectedGroupColor: FC<{ groupColor: GroupColor; groupName: string }> = ({ groupColor, groupName }) => (
  <div title={groupName}>
    <ColorItem id="selected-group-color" color={groupColor} diameter="20px" className="ms-2" />
  </div>
);
SelectedGroupColor.displayName = 'SelectedGroupColor';

const StyledDropdownMenu = styled(DropdownMenu)`
  right: 0 !important;
  left: auto !important;
`;

interface FooterDropDownProps {
  isPinned: boolean;
  availableGroups: PinnedGroup[];
  selectedGroup?: string;
  onPinToggled: () => void;
  onGroupChanged(group: string, color?: GroupColor): void;
  onUnGrouped(): void;
}

const PinnedDropDown: FC<FooterDropDownProps> = ({
  isPinned,
  availableGroups,
  selectedGroup,
  onPinToggled,
  onGroupChanged,
  onUnGrouped,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = useCallback(() => setDropdownOpen(prevState => !prevState), []);

  const onGroupCreated = useCallback(
    (group: string, color?: GroupColor) => {
      onGroupChanged(group, color);
      setDropdownOpen(false);
    },
    [onGroupChanged],
  );

  return (
    <Dropdown show={dropdownOpen} onToggle={toggle}>
      <Dropdown.Toggle
        className={classNames('btn btn-xl btn-flat-secondary btn-icon ps-3', { 'px-1': !isPinned && !selectedGroup })}
        color=""
        data-e2e="pin-dropdown-toggle"
        data-tour="quoteDashboard.tile.pin"
        variant="flat-secondary"
      >
        {(isPinned || selectedGroup) && <em className="icon ms-1">push_pin</em>}
        {!isPinned && !selectedGroup && <IconWorkaround missingIconName="push_pin_outlined" className="ms-2 pe-2" />}
      </Dropdown.Toggle>
      <StyledDropdownMenu data-e2e="pin-dropdown-menu">
        <Dropdown.Item onClick={onPinToggled} active={isPinned} data-e2e="toggle-pinned-dropdown-item">
          <FormattedMessage id="dashboard.pinThisQuote" />
        </Dropdown.Item>
        <Dropdown.Divider />

        <NewGroupDropdownItem onGroupCreated={onGroupCreated} />
        <Dropdown.Divider />
        {availableGroups.map(group => (
          <GroupDropdownItem
            key={group.name}
            group={group}
            isActive={group.name === selectedGroup}
            onClick={() => onGroupChanged(group.name, group.color)}
          />
        ))}
        {selectedGroup && (
          <>
            <Dropdown.Divider />
            <Dropdown.Item onClick={onUnGrouped} data-e2e="ungroup-dropdown-item">
              <FormattedMessage id="dashboard.unGroup" />
            </Dropdown.Item>
          </>
        )}
      </StyledDropdownMenu>
    </Dropdown>
  );
};

PinnedDropDown.displayName = 'PinnedDropDown';

const NoColorItem = styled.div<{ $width: string }>`
  width: ${({ $width }) => $width};
`;

interface GroupDropdownItemProps {
  group: PinnedGroup;
  isActive: boolean;
  onClick(): void;
}
const GroupDropdownItem: FC<GroupDropdownItemProps> = ({ group, isActive, onClick }) => {
  const { formatMessage } = useIntl();
  return (
    <Dropdown.Item className={classNames({ active: isActive })} onClick={onClick} data-e2e="group-dropdown-item">
      <div className="d-flex justify-content-start align-items-center">
        {group.color !== undefined && <ColorItem color={group.color} diameter="15px" />}
        {group.color === undefined && <NoColorItem $width="15px" />}
        <span className="ms-2" title={formatMessage({ id: 'dashboard.groupDropdownItem.title' })}>
          {group.name}
        </span>
      </div>
    </Dropdown.Item>
  );
};

const CustomGroupContainer = styled.div`
  min-width: 15rem !important;
`;

interface NewGroupDropdownItemProps {
  onGroupCreated(group: string, color?: GroupColor): void;
}

const NewGroupDropdownItem: FC<NewGroupDropdownItemProps> = ({ onGroupCreated }) => {
  const { formatMessage } = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [groupColor, setGroupColor] = useState<GroupColor | undefined>(undefined);

  const onClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setIsEditing(true);
  }, []);

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setEditingGroupName(e.target.value);
  }, []);

  const onValidate = useCallback(() => {
    const sanitizedGroupName = editingGroupName.trim();
    if (sanitizedGroupName) {
      onGroupCreated(sanitizedGroupName, groupColor);
    }
  }, [editingGroupName, groupColor, onGroupCreated]);

  const onInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'Enter':
        case 'Tab':
          onValidate();
          break;
        case 'Escape':
          {
            setIsEditing(false);
            setEditingGroupName('');
          }
          break;
      }
    },
    [onValidate],
  );

  return (
    <Dropdown.Item onClick={onClick} as="div" className="clickable" data-e2e="create-new-group-dropdown-item">
      {!isEditing && <FormattedMessage id="dashboard.newGroup" />}
      {isEditing && (
        <div>
          <CustomGroupContainer className="form-group m-0">
            <div className="input-group">
              <input
                type="text"
                autoFocus
                className="form-control"
                placeholder={formatMessage({ id: 'dashboard.newGroup' })}
                value={editingGroupName}
                onChange={onChange}
                onKeyDown={onInputKeyDown}
                data-e2e="new-group-name-input"
              />
              <button className="btn btn-primary" type="button" onClick={onValidate} data-e2e="validate-new-group-btn">
                {formatMessage({ id: 'ok' })}
              </button>
            </div>
          </CustomGroupContainer>
          <ColorSelector onColorSelected={setGroupColor} selectedColor={groupColor} className="mt-3" />
        </div>
      )}
    </Dropdown.Item>
  );
};

NewGroupDropdownItem.displayName = 'NewGroupDropdownItem';
